import { useState } from 'react'

import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import {
  useAcceptCashInstallmentsOfferMutation,
  useSkipCashInstallmentsOfferMutation,
} from '@twisto/api-graphql'
import { Box } from '@twisto/components/atoms/box'
import { TextButton } from '@twisto/components/atoms/button'
import { Typography } from '@twisto/components/atoms/typography'
import { BigTickIcon } from '@twisto/icons'
import { numberOfMonths } from '@twisto/locale'

import { useFlow } from '../../flow/flow.context'

import {
  InstallmentOfferScheduleTable,
  InstallmentsContractTerms,
  InstallmentsSchedulePicker,
} from './../../components'
import * as styles from './cash-installments-offer-step.styles'

export const CashInstallmentsOfferStep = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<number>()
  const { sessionId, flow } = useFlow<'CashInstallmentsOfferStep'>()
  const [skipInstallmentsOffer, { loading }] =
    useSkipCashInstallmentsOfferMutation()
  const [acceptCashInstallmentsOffer, { loading: acceptLoading }] =
    useAcceptCashInstallmentsOfferMutation()

  const currentStepData = flow.currentStep

  if (isNil(currentStepData)) {
    return null
  }

  const offers = currentStepData.offers

  const upToMonths = Number(offers[offers.length - 1].periodsCount)

  const min = Number(offers[0].periodsCount)

  const max = Number(offers[offers.length - 1].periodsCount)

  const periodOffer = offers.find(
    (offer) => offer.periodsCount === (selectedPeriod ?? min)
  )

  const onIgnoreInstallments = () => {
    skipInstallmentsOffer({
      variables: {
        auth: {
          sessionId,
        },
      },
    })
  }

  const onAcceptInstallments = () => {
    if (isNil(periodOffer)) {
      return
    }

    acceptCashInstallmentsOffer({
      variables: {
        auth: {
          sessionId,
        },
        input: {
          periodsCount: periodOffer.periodsCount,
        },
      },
    })
  }

  return (
    <div>
      <Box
        color="primary"
        css={styles.successBox}
        startAdornment={<BigTickIcon color="primary" size="56" />}
      >
        <Typography color="textSecondary" variant="b3">
          <Trans id="flow.cashInstallmentsOfferStep.successBox" />
        </Typography>
      </Box>
      <div>
        {!isNil(periodOffer) && (
          <InstallmentOfferScheduleTable
            amountPerPeriod={periodOffer.amountPerPeriod}
            annualPercentageRate={periodOffer.annualPercentageRate}
            deferVariant={false}
            extraAmount={periodOffer.extraAmount}
            periodsCount={periodOffer.periodsCount}
            principalAmount={periodOffer.principalAmount}
            title={
              <Trans
                id="flow.cashInstallmentsOfferStep.installmentOfferScheduleTable.title"
                values={{ months: numberOfMonths(upToMonths) }}
              />
            }
            totalAmount={periodOffer.totalAmount}
            variant="grayBackground"
          />
        )}

        <InstallmentsSchedulePicker
          max={max}
          min={min}
          value={periodOffer?.periodsCount ?? min}
          onChange={setSelectedPeriod}
        />

        <div css={styles.buttonSection}>
          <TextButton
            fullWidth
            color="primary"
            data-testid="installment-create-schedule-button"
            disabled={loading || acceptLoading}
            onClick={onAcceptInstallments}
          >
            <Trans id="flow.cashInstallmentsOfferStep.createButton" />
          </TextButton>

          <TextButton
            fullWidth
            color="secondary"
            disabled={loading || acceptLoading}
            onClick={onIgnoreInstallments}
          >
            <Trans id="flow.cashInstallmentsOfferStep.notInterestedButton" />
          </TextButton>

          {!isNil(periodOffer) && !isNil(periodOffer.contractTerms) && (
            <InstallmentsContractTerms
              contractTerms={periodOffer.contractTerms}
            />
          )}
        </div>
      </div>
    </div>
  )
}
